// src/stores/auth.store.ts — Mock (local dev)
// Full mock login — no Clerk required for local development.
// When ready for real auth, ClerkAuthGate replaces the login flow.

import { create } from 'zustand'

// ─── Mock users ───────────────────────────────────────────────────────────────
// WHS Module Revision v2.0 — Tier C_WHSO added (WHS Officer)
// C_WHSO has full WHS module access, People read, Competency Matrix.
// C_WHSO has NO access to commercial modules (Estimating, Contracts, Claims, Xero).
const MOCK_USERS = [
  { email: 'admin@trafficprocure.local', password: 'Admin1234!', tier: 'A_DEVELOPER',   role: 'developer',           fullName: 'System Admin',    depotCode: 'CAL', assignedJobs: null },
  { email: 'pm@trafficprocure.local',    password: 'PM1234!pm',  tier: 'B_PM',          role: 'project_manager',     fullName: 'Jane Smith',      depotCode: 'CAL', assignedJobs: null },
  { email: 'pm2@trafficprocure.local',   password: 'PM1234!pm',  tier: 'B_PM',          role: 'project_manager',     fullName: 'Robert Chen',     depotCode: 'BNE', assignedJobs: null },
  { email: 'pc@trafficprocure.local',    password: 'PC1234!pc',  tier: 'B_PC',          role: 'project_coordinator', fullName: 'Sarah Lee',       depotCode: 'CAL', assignedJobs: ['j1','j2','j6'] },
  { email: 'pc2@trafficprocure.local',   password: 'PC1234!pc',  tier: 'B_PC',          role: 'project_coordinator', fullName: 'Mike Walsh',      depotCode: 'BNE', assignedJobs: ['j3','j4','j5'] },
  // ── NEW: WHS Officer (Tier C_WHSO) ─────────────────────────────────────────
  { email: 'whso@trafficprocure.local',  password: 'WHSO1234!',  tier: 'C_WHSO',        role: 'whs_officer',         fullName: 'Alex Morgan',     depotCode: 'CAL', assignedJobs: null },
  // ── Procurement (kept as C_PROCUREMENT for backward compatibility) ──────────
  { email: 'proc@trafficprocure.local',  password: 'Proc1234!',  tier: 'C_PROCUREMENT', role: 'procurement',         fullName: 'Mark Wilson',     depotCode: 'CAL', assignedJobs: null },
  { email: 'field@trafficprocure.local', password: 'Field123!',  tier: 'D_FIELD',       role: 'field',               fullName: 'Tony Rivera',     depotCode: 'CAL', assignedJobs: null },
]

// ─── Utility functions ────────────────────────────────────────────────────────
export function initialsFrom(name: string): string {
  return name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase() ?? '').join('')
}

export function tierLabel(tier: string): string {
  const labels: Record<string, string> = {
    A_DEVELOPER:   'Developer / Admin',
    B_PM:          'Project Manager',
    B_PC:          'Project Coordinator',
    C_WHSO:        'WHS Officer',
    C_PROCUREMENT: 'Procurement',
    D_FIELD:       'Field Technician',
  }
  return labels[tier] ?? tier
}

// ─── Tier access ──────────────────────────────────────────────────────────────
const TIER_ACCESS: Record<string, string[]> = {
  A_DEVELOPER:    ['A_DEVELOPER', 'B_PM', 'B_PC', 'C_WHSO', 'C_PROCUREMENT', 'D_FIELD'],
  B_PM:           ['B_PM', 'B_PC', 'C_WHSO', 'C_PROCUREMENT', 'D_FIELD'],
  B_PC:           ['B_PC'],
  C_WHSO:         ['C_WHSO'],
  C_PROCUREMENT:  ['C_PROCUREMENT'],
  D_FIELD:        ['D_FIELD'],
}

// ─── Types ────────────────────────────────────────────────────────────────────
export interface PlatformUser {
  id:           string
  clerkId:      string
  email:        string
  fullName:     string
  firstName:    string
  lastName:     string
  avatarUrl:    string | null
  tier:         string
  clerkTier:    string
  role:         string
  depotCode:    string
  scopedJobIds: string[] | null
  assignedJobs: string[] | null
}

interface AuthState {
  user:           PlatformUser | null
  isLoading:      boolean
  error:          string | null
  requiresMfa:    boolean
  testMode:       boolean           // Phase 4: BETA banner flag
  setUser:        (user: PlatformUser) => void
  clearUser:      () => void
  clearError:     () => void
  login:          (email: string, password: string, mfa?: string) => Promise<void>
  logout:         () => void
  isTier:         (tier: string) => boolean
  scopedJobIds:   () => string[] | null
  canAssignJobs:  () => boolean
  toggleTestMode: () => void
}

// ─── Store ────────────────────────────────────────────────────────────────────
export const useAuthStore = create<AuthState>((set, get) => ({
  user:        null,
  isLoading:   false,
  error:       null,
  requiresMfa: false,
  testMode:    false,

  setUser: (user) => set({ user }),
  clearUser: () => set({ user: null }),
  clearError: () => set({ error: null }),
  toggleTestMode: () => set(s => ({ testMode: !s.testMode })),

  login: async (email, password) => {
    set({ isLoading: true, error: null })
    await new Promise(r => setTimeout(r, 400)) // brief delay feels more real

    const match = MOCK_USERS.find(
      u => u.email.toLowerCase() === email.toLowerCase() && u.password === password
    )

    if (!match) {
      set({ isLoading: false, error: 'Incorrect email or password.' })
      return
    }

    const nameParts = match.fullName.split(' ')
    set({
      isLoading: false,
      error: null,
      user: {
        id:           `mock_${match.email}`,
        clerkId:      `mock_${match.email}`,
        email:        match.email,
        fullName:     match.fullName,
        firstName:    nameParts[0] ?? '',
        lastName:     nameParts.slice(1).join(' ') ?? '',
        avatarUrl:    null,
        tier:         match.tier,
        clerkTier:    match.tier[0],
        role:         match.role,
        depotCode:    match.depotCode,
        scopedJobIds: match.assignedJobs,
        assignedJobs: match.assignedJobs,
      },
    })
  },

  logout: () => set({ user: null, error: null }),

  isTier: (tier) => {
    const { user } = get()
    if (!user) return false
    return (TIER_ACCESS[user.tier] ?? []).includes(tier)
  },

  scopedJobIds: () => {
    const { user } = get()
    if (!user) return null
    if (user.tier === 'B_PC') return user.scopedJobIds
    return null
  },

  canAssignJobs: () => {
    const { user } = get()
    if (!user) return false
    return ['A_DEVELOPER', 'B_PM'].includes(user.tier)
  },
}))
